import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaPlus, FaDollarSign, FaFileExport } from 'react-icons/fa';
import { BsSearch, BsCalculator } from "react-icons/bs";
import { Link } from 'react-router-dom';
import { MdAssignment } from "react-icons/md";
import { 
  Button, 
  ButtonGroup, 
  Badge, 
  Chip, 
  div, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Typography
} from '@mui/material';
import { BillingUtils } from '../Services/billingService';

// TODO: Crear componente CreateBillableActivityModal
// Ubicación: src/components/BillableActivities/Popup/CreateBillableActivityModal.js
// Props esperadas: { show, handleClose, onSave }
// Propósito: Modal para crear nuevas actividades facturables
import CreateBillableActivityModal from '../Popup/CreateBillableActivityModal';

// TODO: Crear componente BillingFilter
// Ubicación: src/components/BillableActivities/Buttons/BillingFilter.js
// Props esperadas: { initialFilters, onFilterChange }
// Propósito: Componente de filtros avanzados para actividades facturables
import BillingFilter from '../Buttons/BillingFilter';

// TODO: Crear componente BillingActionButton
// Ubicación: src/components/BillableActivities/Buttons/BillingActionButton.js
// Props esperadas: { selectedActivities, onBulkReprice, onGenerateSalesOrders }
// Propósito: Botón con acciones masivas de facturación
import BillingActionButton from '../Buttons/BillingActionButton';

// TODO: Crear componente UniversalSearch
// Ubicación: src/components/BillableActivities/Buttons/UniversalSearch.js
// Props esperadas: { onSearch, placeholder }
// Propósito: Barra de búsqueda universal con funcionalidad de autocompletado
import UniversalSearch from '../Buttons/UniversalSearch';

import BillingPivotTable from '../Pivot/BillingPivotTable';

const BillingFiltersHeader = ({ 
  onFilterChange = () => {}, 
  onSearchChange = () => {}, 
  selectedActivities = [], 
  onClearSelection = () => {}, 
  onBulkReprice = () => {}, 
  onGenerateSalesOrders = () => {}, 
  onReloadData = () => {}, 
  currentUserRoles = [], 
  currentCenterId = null, // Agregar esta línea
  isProcessingBulkAction = false, 
  activities = [], // ⬅️ AGREGAR ESTA PROP
  processedActivities = [] // ⬅️ AGREGAR ESTA PROP
}) => {
  // Estado para todos los filtros disponibles de facturación
  const [currentFilters, setCurrentFilters] = useState({
    // Filtros de entidades principales
    patient_id: '',
    patient_name: '',
    doctor_id: '',
    doctor_name: '',
    center_id: '',
    center_name: '',
    payer_id: '',
    payer_name: '',
    
    // Filtros de fechas
    performed_at_from: '',
    performed_at_to: '',
    created_at_from: '',
    created_at_to: '',
    
    // Filtros de actividad
    activity_type: '',
    description: '',
    status: '',
    
    // Filtros monetarios
    min_amount: '',
    max_amount: '',
    has_pending_amount: '',
    
    // Filtros de autorización
    has_authorization: '',
    authorization_code: '',
    
    // Filtros de órdenes de venta
    has_sales_orders: '',
    so_status: '',
    
    // Filtros de facturación
    is_fully_invoiced: '',
    is_partially_invoiced: '',
    is_non_billable: '',
    
    // Búsqueda universal
    search: ''
  });

  // Estado para los filtros activos (que tienen valores)
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Estado para mostrar/ocultar la sección de filtros activos
  const [showActiveFilters, setShowActiveFilters] = useState(true);
  
  // Estado para mostrar/ocultar el modal de creación
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Estado para mostrar/ocultar el offcanvas de filtros
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);

  // Estado para mostrar/ocultar el diálogo de pivot
  const [showPivotDialog, setShowPivotDialog] = useState(false);

  // Estados para diálogos de acciones masivas
  const [showBulkActionsDialog, setShowBulkActionsDialog] = useState(false);
  const [showGenerateSalesOrderDialog, setShowGenerateSalesOrderDialog] = useState(false);
  const [salesOrderGroupingMode, setSalesOrderGroupingMode] = useState('PAYER_PATIENT');

  // Refs
  const offcanvasRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Función para convertir objeto de filtros a array para mostrar
  const convertFiltersToArray = (filters) => {
    const filterLabels = {
      patient_name: 'Patient',
      doctor_name: 'Doctor',
      center_name: 'Center',
      payer_name: 'Payer',
      performed_at_from: 'From Date',
      performed_at_to: 'To Date',
      activity_type: 'Activity Type',
      description: 'Description',
      status: 'Status',
      min_amount: 'Min Amount',
      max_amount: 'Max Amount',
      has_authorization: 'Has Authorization',
      authorization_code: 'Auth Code',
      search: 'Search'
    };

    return Object.entries(filters)
      .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      .map(([key, value]) => ({ 
        label: filterLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
        value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value,
        key 
      }));
  };

  // Helper function to safely call onFilterChange
  const safeCallOnFilterChange = (filters) => {
    if (onFilterChange && typeof onFilterChange === 'function') {
      onFilterChange(filters);
    }
  };

  // Helper function to safely call onSearchChange
  const safeCallOnSearchChange = (searchTerm) => {
    if (onSearchChange && typeof onSearchChange === 'function') {
      onSearchChange(searchTerm);
    }
  };

  // Helper function to safely call onReloadData
  const safeCallOnReloadData = () => {
    if (onReloadData && typeof onReloadData === 'function') {
      onReloadData();
    }
  };

  // Helper function to safely call onClearSelection
  const safeCallOnClearSelection = () => {
    if (onClearSelection && typeof onClearSelection === 'function') {
      onClearSelection();
    }
  };

  // Maneja cambios en los filtros
  const handleFilterChange = (filters) => {
    setCurrentFilters(filters);
    
    // Actualiza los filtros activos
    const newActiveFilters = convertFiltersToArray(filters);
    setActiveFilters(newActiveFilters);
    
    // Notifica al componente padre
    safeCallOnFilterChange(filters);
  };

  // Maneja la búsqueda universal
  const handleUniversalSearch = (searchTerm) => {
    const updatedFilters = { 
      ...currentFilters, 
      search: searchTerm 
    };
    
    setCurrentFilters(updatedFilters);
    
    // Actualiza los filtros activos
    const newActiveFilters = convertFiltersToArray(updatedFilters);
    setActiveFilters(newActiveFilters);

    // Notifica al componente padre
    safeCallOnSearchChange(searchTerm);
  };

  // Remueve un filtro específico
  const removeFilter = (filterToRemove) => {
    const updatedFilters = { ...currentFilters };
    
    // Limpia el valor del filtro
    updatedFilters[filterToRemove.key] = '';
    
    setCurrentFilters(updatedFilters);
    
    // Actualiza los filtros activos
    const newActiveFilters = convertFiltersToArray(updatedFilters);
    setActiveFilters(newActiveFilters);
    
    // Notifica al componente padre
    safeCallOnFilterChange(updatedFilters);
    
    // Si no quedan filtros activos, recarga todos los datos
    if (newActiveFilters.length === 0) {
      safeCallOnReloadData();
    }
  };

  // Limpia todos los filtros
  const clearAllFilters = () => {
    const resetFilters = {
      patient_id: '',
      patient_name: '',
      doctor_id: '',
      doctor_name: '',
      center_id: '',
      center_name: '',
      payer_id: '',
      payer_name: '',
      performed_at_from: '',
      performed_at_to: '',
      created_at_from: '',
      created_at_to: '',
      activity_type: '',
      description: '',
      status: '',
      min_amount: '',
      max_amount: '',
      has_pending_amount: '',
      has_authorization: '',
      authorization_code: '',
      has_sales_orders: '',
      so_status: '',
      is_fully_invoiced: '',
      is_partially_invoiced: '',
      is_non_billable: '',
      search: ''
    };

    setCurrentFilters(resetFilters);
    setActiveFilters([]);
    safeCallOnFilterChange(resetFilters);
    
    // Limpia las selecciones si existe la función
    safeCallOnClearSelection();
    
    // Recarga todos los datos
    safeCallOnReloadData();
  };

  // Abre el modal de creación
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  // Cierra el modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Maneja el guardado de nueva actividad facturable
  const handleSaveActivity = (activityData) => {
    console.log('Billing Activity Data:', activityData);
    // TODO: Implementar lógica de guardado real
    
    // Cierra el modal después de guardar
    setShowCreateModal(false);
    
    // Recarga los datos
    safeCallOnReloadData();
  };

  // Toggle para mostrar/ocultar filtros activos
  const toggleActiveFiltersVisibility = () => {
    setShowActiveFilters(!showActiveFilters);
  };

  // Toggle para mostrar/ocultar offcanvas de filtros
  const toggleFilterOffcanvas = () => {
    setShowFilterOffcanvas(!showFilterOffcanvas);
  };

  // Cerrar offcanvas
  const closeFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  // Maneja acciones masivas
  const handleBulkReprice = () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to reprice');
      return;
    }
    
    if (window.confirm(`Reprice ${selectedActivities.length} selected activities?`)) {
      if (onBulkReprice && typeof onBulkReprice === 'function') {
        onBulkReprice(selectedActivities);
      }
    }
    setShowBulkActionsDialog(false);
  };

  const handleGenerateSalesOrders = () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to generate sales orders');
      return;
    }
    
    setShowGenerateSalesOrderDialog(true);
  };

  const handleConfirmGenerateSalesOrders = () => {
    if (onGenerateSalesOrders && typeof onGenerateSalesOrders === 'function') {
      onGenerateSalesOrders({
        activityIds: selectedActivities,
        groupingMode: salesOrderGroupingMode
      });
    }
    setShowGenerateSalesOrderDialog(false);
    setShowBulkActionsDialog(false);
  };

  // Combina filtros activos con actividades seleccionadas
  const allActiveFilters = [
    ...activeFilters,
    ...(selectedActivities.length > 0 
      ? [{ 
          label: 'Selected Activities', 
          value: selectedActivities.length, 
          key: 'selectedActivities' 
        }] 
      : [])
  ];

  // Verificar permisos
  const canGenerateSalesOrders = BillingUtils?.canGenerateSalesOrders?.(currentUserRoles) || false;
  const canReprice = BillingUtils?.canReprice?.(currentUserRoles) || false;

  // Efecto mejorado para manejar clic fuera del offcanvas
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo procesar si el offcanvas está abierto
      if (!showFilterOffcanvas) return;

      const target = event.target;
      
      // Verificar si el clic fue dentro del offcanvas
      const isClickInsideOffcanvas = offcanvasRef.current && 
        offcanvasRef.current.contains(target);
      
      // Verificar si el clic fue en el botón del filtro
      const isClickOnFilterButton = filterButtonRef.current && 
        filterButtonRef.current.contains(target);
      
      // Si el clic no fue en el offcanvas ni en el botón del filtro, cerrar
      if (!isClickInsideOffcanvas && !isClickOnFilterButton) {
        setShowFilterOffcanvas(false);
      }
    };

    // Solo agregar el listener si el offcanvas está abierto
    if (showFilterOffcanvas) {
      // Usar un pequeño delay para evitar que el clic que abre el offcanvas lo cierre inmediatamente
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFilterOffcanvas]);

  // Efecto para manejar tecla Escape
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showFilterOffcanvas) {
          setShowFilterOffcanvas(false);
        }
        if (showCreateModal) {
          setShowCreateModal(false);
        }
        if (showBulkActionsDialog) {
          setShowBulkActionsDialog(false);
        }
        if (showGenerateSalesOrderDialog) {
          setShowGenerateSalesOrderDialog(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showFilterOffcanvas, showCreateModal, showBulkActionsDialog, showGenerateSalesOrderDialog]);

  // Efecto para agregar/remover clase del body cuando el offcanvas está abierto
  useEffect(() => {
    if (showFilterOffcanvas) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup cuando el componente se desmonta
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilterOffcanvas]);

  return (
    <>     
      {/* Header principal con búsqueda y botones */}
      <div className="filter-container py-3">
        <div className="row align-items-center">
          <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
            <UniversalSearch 
              onSearch={handleUniversalSearch}
              placeholder="Search billing activities..."
            />
          </div>

          {/* Botones de acción */}
          <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 gy-3 flex-wrap">
            
            {/* Botones de acción */}
              <BillingActionButton
                selectedActivities={selectedActivities}
                onBulkReprice={onBulkReprice}
                onGenerateSalesOrders={onGenerateSalesOrders}
                currentUserRoles={currentUserRoles}
                isProcessingBulkAction={isProcessingBulkAction}
              />
              <button
                className="btn btn-info d-flex align-items-center gap-1"
                onClick={() => setShowPivotDialog(true)}
              >
                <span className="material-symbols-outlined icon-md">pivot_table_chart</span>
                <span>Pivot Table</span>
              </button>
            

            {/* Botones de filtros */}
            <Link 
                            to="#" 
                            className="btn btn-gray filterbtn d-flex align-items-center gap-1 position-relative"
                            type="button"
                onClick={toggleFilterOffcanvas}
              >
                <div className="filter_text d-flex align-items-center gap-1">
                                                                        <span className="d-block material-symbols-outlined icon-md">tune</span>
                                                                        <span className="d-block">Filter</span>
                                                                    </div>
                                {/* Show badge only if there are active filters */}
                                {allActiveFilters.length > 0 && (
                                  <span 
                                    className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-primary" 
                                    style={{fontSize: '0.6rem', padding: '0.2rem 0.4rem'}}
                                  >
                                    {allActiveFilters.length}
                                  </span>
                                )}
                              </Link>

            {/* Botón para crear nueva actividad facturable */}
            <button
        className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
              type="button"
              variant="contained"
              color="primary"
              onClick={handleOpenCreateModal}
            >
        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
      </button>
          </div>
        </div>
      </div>

      {/* Backdrop para el offcanvas */}
      {showFilterOffcanvas && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={closeFilterOffcanvas}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1040,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
      )}

      {/* Offcanvas de filtros */}
      <div 
        className={`offcanvas offcanvas-end ${showFilterOffcanvas ? 'show' : ''}`}
        tabIndex="-1" 
        id="filtersOffcanvas"
        ref={offcanvasRef}
        style={{
          visibility: showFilterOffcanvas ? 'visible' : 'hidden',
          width: '400px',
          transform: showFilterOffcanvas ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1045,
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          backgroundColor: 'white'
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">Billing Filters</h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={closeFilterOffcanvas}
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body p-0">
          <div className="p-3">
            <BillingFilter 
              initialFilters={currentFilters}
              onFilterChange={handleFilterChange}
              isVisible={showFilterOffcanvas}
              onClose={closeFilterOffcanvas}
            />
          </div>
        </div>
      </div>

      {/* Modal de creación */}
      <CreateBillableActivityModal 
        show={showCreateModal}
        handleClose={handleCloseCreateModal}
        onSave={handleSaveActivity}
        currentUserRoles={currentUserRoles}
        currentCenterId={currentCenterId} // Pasar el centro actual
      />

      {/* Dialog de acciones masivas */}
      <Dialog 
        open={showBulkActionsDialog} 
        onClose={() => setShowBulkActionsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Actions ({selectedActivities.length} activities)</DialogTitle>
        <DialogContent>
          <div sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {canReprice && (
              <Button
                variant="outlined"
                startIcon={<BsCalculator />}
                onClick={handleBulkReprice}
                disabled={isProcessingBulkAction}
                fullWidth
              >
                Reprice Selected Activities
              </Button>
            )}
            
            {canGenerateSalesOrders && (
              <Button
                variant="outlined"
                startIcon={<MdAssignment />}
                onClick={handleGenerateSalesOrders}
                disabled={isProcessingBulkAction}
                fullWidth
              >
                Generate Sales Orders
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<FaFileExport />}
              onClick={() => {
                // TODO: Implementar exportación
                console.log('Export selected activities:', selectedActivities);
                setShowBulkActionsDialog(false);
              }}
              disabled={isProcessingBulkAction}
              fullWidth
            >
              Export Selected Activities
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkActionsDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para generar órdenes de venta */}
      <Dialog 
        open={showGenerateSalesOrderDialog} 
        onClose={() => setShowGenerateSalesOrderDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Sales Orders</DialogTitle>
        <DialogContent>
          <div sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate sales orders for {selectedActivities.length} selected activities.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Grouping Mode</InputLabel>
              <Select
                value={salesOrderGroupingMode}
                onChange={(e) => setSalesOrderGroupingMode(e.target.value)}
                label="Grouping Mode"
              >
                <MenuItem value="PAYER_PATIENT">By Payer + Patient</MenuItem>
                <MenuItem value="PAYER_ONLY">By Payer Only</MenuItem>
                <MenuItem value="EPISODE">By Episode</MenuItem>
                <MenuItem value="CUSTOM">Custom</MenuItem>
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGenerateSalesOrderDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmGenerateSalesOrders}
            variant="contained"
            disabled={isProcessingBulkAction}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sección de filtros activos */}
      {showActiveFilters && allActiveFilters.length > 0 && (
        <div sx={{ px: 2, pb: 2 }}>
          <div sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            flexWrap: 'wrap',
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Active Filters:
            </Typography>
            
            {/* Renderiza cada filtro activo como chip */}
            {allActiveFilters.map((filter, index) => (
              <Chip
                key={index}
                label={`${filter.label}: ${filter.value}`}
                onDelete={() => {
                  if (filter.key === 'selectedActivities') {
                    safeCallOnClearSelection();
                  } else {
                    removeFilter(filter);
                  }
                }}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
            
            {/* Botón para limpiar todos los filtros */}
            {allActiveFilters.length > 0 && (
              <Button 
                size="small"
                onClick={clearAllFilters}
                sx={{ ml: 'auto' }}
              >
                Clear All
              </Button>
            )}
          </div>
        </div>
      )}
      <BillingPivotTable
        open={showPivotDialog}
        onClose={() => setShowPivotDialog(false)}
        activities={processedActivities.length > 0 ? processedActivities : activities} // ✅ Usar processedActivities primero
        selectedActivities={selectedActivities || []}
      />
    </>    
  );
};

export default BillingFiltersHeader;